import type { PermissionsKey } from '@vedalib/crud'
import { EmployeeStatusEnum, getCommentRole, getTaskRole, PermissionService } from '@vedalib/crud'
import * as R from 'ramda'
import { createContext, useContext, useMemo } from 'react'

import type { CommentMock } from 'components/comments/types'
import type { KitAccessState } from 'components/uiKit/KitTypes'
import type { TaskMock } from 'components/uiKit/Task'
import type { EmployeeListSchemaFragment } from 'gql/__generated__/graphql'
import { CompanyRole } from 'gql/__generated__/graphql'
import { useParticipantsAll } from 'gql/participants/apollo'
import { selectCurrentEmployee, selectSortedEmployeesAll } from 'services/Store/Users/selectors'
import { useAppSelector } from 'services/Store/hooks'
import { notEmpty } from 'utils/notEmpty'

import type { FinderFileItem } from '../../store/models'
import type {
  ICheckPermissionsParam,
  IUiPermission,
  LoadIdentityData,
  PermissionLeaf,
} from './types'

export type PermissionContextValue = LoadIdentityData

interface IUseEmployeesByPermissionInProject {
  projectId: string
  includeCommentator?: boolean
  excludeSelf?: boolean
  pk: PermissionsKey | PermissionsKey[]
  sort?: (<U>(a: U, b: U) => R.Ordering)[]
}

export const PermissionContext = createContext({
  identity: {},
  loading: true,
  permissionKeys: [] as PermissionsKey[],
} as PermissionContextValue)

const usePermissionContext = () => useContext(PermissionContext)

const checker = (service: PermissionService, perm: PermissionLeaf) => service.has(perm)

const isOptions = <I,>(
  item: I | (I & IUiPermission)[] | PermissionLeaf,
): item is (I & IUiPermission)[] => Array.isArray(item) && typeof item[0] === 'object'

const isMany = <I extends Record<string, PermissionLeaf> | (I & IUiPermission)[] | PermissionLeaf>(
  item: Record<string, PermissionLeaf> | (I & IUiPermission)[] | PermissionLeaf,
): item is Record<string, PermissionLeaf> => !Array.isArray(item) && typeof item === 'object'

const applyPermissions = <I extends KitAccessState>(
  service: PermissionService,
  { permission: p, ...rest }: I & IUiPermission,
) =>
  ({
    ...rest,
    ...(p?.disabled && { disabled: !checker(service, p.disabled) || rest.disabled }),
    ...(p?.hidden && { hidden: !checker(service, p.hidden) || rest.hidden }),
    ...(p?.readonly && { readonly: !checker(service, p.readonly) || rest.readOnly }),
  }) as I & KitAccessState

/**
 * @example
 *  Usage with `options`
 * ```typescript
 * const OPTIONS: IMenuOptions<'edit' | 'delete'> = [
 *  {
 *    value: 'edit',
 *    permission: {
 *      disabled: { key: [PermissionsKey.project_resource_c, PermissionsKey.company_resource_c], mode: 'or'}
 *    }
 *  },
 *  {
 *    value: 'delete',
 *    permission: {
 *      hidden: PermissionsKey.project_resource_c
 *    }
 *  }
 * ];
 * const Component: React.FC = () => {
 *  const options = usePermissions(OPTIONS);
 *  // options: [
 *  //   { value: 'edit', disabled: true },
 *  //   { value: 'delete', hidden: true }
 *  // ]
 *  // return ...
 * ```
 * }
 */
export function usePermissions<I>(options: (I & IUiPermission)[]): (I & KitAccessState)[]
/**
 * @example
 *  Usage with `many`
 * ```typescript
 * const MANY = {
 *  canDelete: PermissionsKey.project_resource_d,
 *  canEdit: PermissionsKey.project_resource_u,
 *  canRead: { key: [PermissionsKey.project_resource_c, PermissionsKey.company_resource_c], mode: 'or'},
 * }
 * const Component: React.FC = () => {
 *  const { canDelete, canEdit, canRead } = usePermissions(MANY);
 *  // canDelete: true, canEdit: true, canRead: false
 *  // return ...
 * }
 * ```
 */
export function usePermissions<I extends Record<string, PermissionLeaf>>(
  many: I,
): Record<keyof I, boolean>
/**
 * @example
 *  Usage with `key` and `mode`
 * ```typescript
 * const Component: React.FC = () => {
 *  const canDelete = usePermissions(PermissionsKey.project_resource_d, 'or');
 *  // canDelete: true
 *  // return ...
 * }
 * ```
 */
export function usePermissions(key: ICheckPermissionsParam, mode?: 'or' | 'and'): boolean
/**
 * @example
 * // Usage without arguments
 * ```typescript
 * const service = usePermissions();
 * // service: PermissionService<PermissionIdentification>
 * // service.has(PermissionsKey.project_resource_d)
 * ```
 */
export function usePermissions(): PermissionService
export function usePermissions<I extends Record<string, PermissionLeaf>>(
  data?: I | (I & IUiPermission)[] | ICheckPermissionsParam,
) {
  const { permissionKeys } = usePermissionContext()

  return useMemo(() => {
    const service = PermissionService.formArray(permissionKeys)
    if (data && isMany(data)) {
      return R.mapObjIndexed((v) => checker(service, v), data)
    }

    if (data && isOptions(data)) {
      return data.map((item) => applyPermissions(service, item))
    }

    return data ? service.has(data) : service
  }, [permissionKeys, data])
}

/**
 * @example
 *  Usage with `options`
 * ```typescript
 * const OPTIONS: IMenuOptions<'edit' | 'delete'> = [
 *  {
 *    value: 'edit',
 *    permission: {
 *      disabled: { key: [PermissionsKey.project_resource_c, PermissionsKey.company_resource_c], mode: 'or'}
 *    }
 *  },
 *  {
 *    value: 'delete',
 *    permission: {
 *      hidden: PermissionsKey.project_resource_c
 *    }
 *  }
 * ];
 * const Component: React.FC = () => {
 *  const options = usePermissions(OPTIONS);
 *  // options: [
 *  //   { value: 'edit', disabled: true },
 *  //   { value: 'delete', hidden: true }
 *  // ]
 *  // return ...
 * ```
 * }
 */
export function useExternalPermissions<I>(
  permissionsKeys: PermissionsKey[],
  options: (I & IUiPermission)[],
): (I & KitAccessState)[]
/**
 * @example
 *  Usage with `many`
 * ```typescript
 * const MANY = {
 *  canDelete: PermissionsKey.project_resource_d,
 *  canEdit: PermissionsKey.project_resource_u,
 *  canRead: { key: [PermissionsKey.project_resource_c, PermissionsKey.company_resource_c], mode: 'or'},
 * }
 * const Component: React.FC = () => {
 *  const { canDelete, canEdit, canRead } = usePermissions(MANY);
 *  // canDelete: true, canEdit: true, canRead: false
 *  // return ...
 * }
 * ```
 */
export function useExternalPermissions<I extends Record<string, PermissionLeaf>>(
  permissionsKeys: PermissionsKey[],
  many: I,
): Record<keyof I, boolean>
/**
 * @example
 *  Usage with `key` and `mode`
 * ```typescript
 * const Component: React.FC = () => {
 *  const canDelete = usePermissions(PermissionsKey.project_resource_d, 'or');
 *  // canDelete: true
 *  // return ...
 * }
 * ```
 */
export function useExternalPermissions(
  permissionsKeys: PermissionsKey[],
  key: ICheckPermissionsParam,
  mode?: 'or' | 'and',
): boolean
/**
 * @example
 * // Usage without arguments
 * ```typescript
 * const service = usePermissions();
 * // service: PermissionService<PermissionIdentification>
 * // service.has(PermissionsKey.project_resource_d)
 * ```
 */
export function useExternalPermissions<I extends Record<string, PermissionLeaf>>(
  permissionsKeys: PermissionsKey[],
  data?: I | (I & IUiPermission)[] | ICheckPermissionsParam,
) {
  return useMemo(() => {
    const service = PermissionService.formArray(permissionsKeys)
    if (data && isMany(data)) {
      return R.mapObjIndexed((v) => checker(service, v), data)
    }

    if (data && isOptions(data)) {
      return data.map((item) => applyPermissions(service, item))
    }

    return data ? service.has(data) : service
  }, [permissionsKeys, data])
}

export const createPermissionHook = (option: {
  disabled?: PermissionLeaf
  hidden?: PermissionLeaf
  readOnly?: PermissionLeaf
}) => {
  const useFieldPermissionHook = () => {
    const data = usePermissions(option)
    return useMemo(
      () => ({
        ...(data.disabled && { disabled: data.disabled }),
        ...(!data.hidden && { hidden: !data.hidden }),
        ...(data.readOnly && { readOnly: data.readOnly }),
      }),
      [data],
    )
  }

  return useFieldPermissionHook
}

export const useCommentPermissions = (
  comment: Pick<CommentMock, 'mentioned' | 'createdBy' | 'project'>,
) => {
  const { identityData, identity, loading } = usePermissionContext()
  if (loading) {
    return PermissionService.formIdentity(identity)
  }

  if (!comment.mentioned || !comment.createdBy) {
    throw new Error('Employee is not defined')
  }

  const mentionedIds = comment.mentioned.map(({ id }) => id)
  const commentRole = getCommentRole(
    { mentionedIds, createdBy: comment.createdBy.id },
    identityData.employee?.id || '',
  )
  return PermissionService.formIdentity({
    ...identity,
    participant: comment.project?.me || undefined,
    commentRole,
  })
}

export const useFileMetaPermissions = (file: Pick<FinderFileItem, 'createdBy'>) => {
  const { identity, loading } = usePermissionContext()
  if (loading || !file) {
    return PermissionService.formIdentity(identity)
  }

  if (!file.createdBy) {
    throw new Error('Employee is not defined')
  }

  return PermissionService.formIdentity(identity)
}

export const useTaskPermissions = (task?: Pick<TaskMock, 'assigned' | 'createdBy'> | null) => {
  const { identity, identityData, loading } = usePermissionContext()
  if (loading || !task) {
    return PermissionService.formIdentity(identity)
  }

  if (!task.assigned || !task.createdBy) {
    throw new Error('Employee is not defined')
  }

  const assignedIds = task.assigned.map((item) => item.id) // TODO: CRUD after tasks
  const taskRole = getTaskRole(
    { assignedIds, createdBy: task.createdBy?.id || '' },
    identityData?.employee?.id || '',
  )
  return PermissionService.formIdentity({ ...identity, taskRole })
}

export const useEmployeesByPermissionInProject = ({
  projectId,
  includeCommentator = false,
  excludeSelf,
  pk,
  sort,
}: IUseEmployeesByPermissionInProject) => {
  const { data, loading } = useParticipantsAll(projectId, {})
  const companyEmployees = useAppSelector(selectSortedEmployeesAll)
  const currentEmployee = useAppSelector(selectCurrentEmployee)
  return useMemo(() => {
    const participantsAll = data?.data.participants.filter(notEmpty) || []
    const project = participantsAll
      .map((p) => {
        const permissions = PermissionService.formIdentity({
          user: p?.employee.kUser,
          employee: p?.employee,
          participant: p,
        })
        if (!includeCommentator && p?.employee.role === CompanyRole.commentator) {
          return
        }

        if (permissions.has(pk)) {
          return p.employee
        }

        return
      })
      .filter(notEmpty)
    const company = companyEmployees.filter((ce) => {
      const permissions = PermissionService.formIdentity({
        user: ce.kUser,
        employee: ce,
      })
      if (permissions.has(pk)) {
        return !project.some((pe) => pe.id === ce.id)
      }

      return false
    })
    const all = [...project, ...company].filter((item) => {
      if (excludeSelf) {
        return item.id !== currentEmployee?.id
      }

      return true
    })
    return {
      company: sort ? R.sortWith(sort, company) : company,
      project: sort ? R.sortWith(sort, project) : project,
      all: sort ? R.sortWith(sort, all) : all,
      loading,
    }
  }, [
    data?.data.participants,
    companyEmployees,
    sort,
    loading,
    includeCommentator,
    pk,
    excludeSelf,
    currentEmployee?.id,
  ])
}

export const SORT_BY_STATUS = [R.ascend(R.propOr(EmployeeStatusEnum.ARCHIVED, 'status'))]

export const getEmployeesByPermissionInCompanyFiltered = ({
  pk,
  includeCommentator = true,
  employees,
}: {
  pk: PermissionsKey | PermissionsKey[]
  includeCommentator?: boolean
  employees: EmployeeListSchemaFragment[]
}) => {
  return employees.filter((employee) => {
    const employeePermissions = PermissionService.formIdentity({
      user: employee.kUser,
      employee,
    })
    if (!includeCommentator && employee.role === CompanyRole.commentator) {
      return false
    }

    if (employeePermissions.has(pk)) {
      return employee
    }

    return false
  })
}

export { usePermissionContext }
